import { useCallback, useState } from 'react';

export type DismissibleBanner = {
  isDismissed: boolean;
  dismiss: () => void;
};

export type IsDismissalInEffect = (dismissedOnUTCDate: string, todayUTCDate: string) => boolean;

export const expiresDaily: IsDismissalInEffect = (dismissedOnUTCDate, todayUTCDate) =>
  dismissedOnUTCDate === todayUTCDate;

export const neverExpires: IsDismissalInEffect = () => true;

function getUTCDateString(): string {
  return new Date().toISOString().slice(0, 10);
}

function buildKey(storageKeyPrefix: string, prerenderUserId: number): string {
  return `${storageKeyPrefix}:${prerenderUserId}`;
}

// A browser with site data disabled throws from localStorage rather than returning null
// (older Safari private mode does this on write). Both accesses below are guarded because the
// read runs in a render-phase state initialiser: unguarded, it takes down the page hosting the
// banner rather than only the banner.
function readDismissalDate(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeDismissalDate(key: string, date: string): void {
  try {
    localStorage.setItem(key, date);
  } catch {
    return;
  }
}

export function useDismissibleBanner(
  storageKeyPrefix: string,
  prerenderUserId: number,
  isDismissalInEffect: IsDismissalInEffect = expiresDaily
): DismissibleBanner {
  const key = buildKey(storageKeyPrefix, prerenderUserId);
  const [isDismissed, setIsDismissed] = useState(() => {
    const dismissedOn = readDismissalDate(key);

    return dismissedOn !== null && isDismissalInEffect(dismissedOn, getUTCDateString());
  });

  const dismiss = useCallback(() => {
    writeDismissalDate(key, getUTCDateString());
    setIsDismissed(true);
  }, [key]);

  return { isDismissed, dismiss };
}
